import { IFlashcardCollectionFilters } from '@sdk/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

interface IStore {
  limit: number;
  sort: Record<string, 'asc' | 'desc'>;
  filters: IFlashcardCollectionFilters;
  setLimit: (limit: number) => void;
  setSort: (sort: Record<string, 'asc' | 'desc'>) => void;
  setFilters: (filters: IFlashcardCollectionFilters) => void;
}

const store = createStore<IStore>(set => ({
  sort: { createdAt: 'desc' },
  limit: INITIAL_LIMIT,
  filters: {},
  setLimit: (limit: number) => set(() => ({ limit })),
  setSort: (sort: Record<string, 'asc' | 'desc'>) => set(() => ({ sort })),
  setFilters: (filters: IFlashcardCollectionFilters) => set(() => ({ filters }))
}));

const FlashcardCollectionService = sdk.getService('FlashcardCollectionService');

export const useMyFlashcardsCollectionList = () => {
  const { sort, limit, filters, setSort, setFilters } = store();

  const { isLoading, data, fetchNextPage } = useInfiniteQuery({
    queryKey: ['flashcards', 'flashcards-collection', sort, limit, filters],
    queryFn: ({ pageParam = INITIAL_PAGE }) =>
      FlashcardCollectionService.getFlashcardsCollections(
        { page: pageParam, limit },
        sort,
        filters
      ),
    getNextPageParam: lastPage => {
      return lastPage.hasNextPage ? lastPage.nextPage : undefined;
    },
    initialPageParam: INITIAL_PAGE,
    refetchOnWindowFocus: false
  });

  const pagination = {
    totalDocs: data?.pages[0].totalDocs ?? 0,
    hasNextPage: data?.pages[data.pages.length - 1]?.hasNextPage ?? false
  };

  return {
    isLoading,
    flashcardsCollectionList: data?.pages.flatMap(itemPage => itemPage.docs) ?? [],
    pagination,
    sort,
    setSort,
    limit,
    fetchNextPage,
    filters,
    setFilters
  };
};
